/**
 * ScheduledJobsList Component
 *
 * Main list view for scheduled jobs with search, filter, and actions.
 * Uses Standard Component Library throughout.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  StandardButton,
  StandardDataTable,
  StandardSearchBar,
  StandardCard,
  StandardLoadingState,
  StandardEmptyState,
  StandardModal,
  useToast,
  type TableColumn
} from '../../standard';
import {
  Plus,
  Play,
  Pause,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Database,
  FileText,
  Mail,
  BarChart3,
  Settings,
  Archive
} from 'lucide-react';
import { scheduledJobsStorage } from '../../../services/scheduledJobsStorage';
import * as scheduledJobsStorageRouter from '../../../services/scheduledJobsStorageRouter';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { ScheduledJob, JobType } from '../../../types/scheduledJobs';
import { mockScheduledJobs, mockJobExecutions } from '../../../data/mockScheduledJobs';

// Icon mapping for job types
const jobTypeIcons: Record<JobType, React.ComponentType<{ size?: number; className?: string }>> = {
  data_retention_purge: Database,
  scheduled_report: FileText,
  data_cleanup: Archive,
  reminder_email: Mail,
  analytics_aggregation: BarChart3,
  custom: Settings
};

const jobTypeLabels: Record<JobType, string> = {
  data_retention_purge: 'Data Retention Purge',
  scheduled_report: 'Scheduled Report',
  data_cleanup: 'Data Cleanup',
  reminder_email: 'Reminder Email',
  analytics_aggregation: 'Analytics Aggregation',
  custom: 'Custom Job'
};

interface ScheduledJobsListProps {
  onCreateJob: () => void;
  onEditJob: (jobId: string) => void;
  onViewJob: (jobId: string) => void;
}

export const ScheduledJobsList: React.FC<ScheduledJobsListProps> = ({
  onCreateJob,
  onEditJob,
  onViewJob
}) => {
  const { success, error: showError } = useToast();

  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJobs, setSelectedJobs] = useState<Set<string>>(new Set());
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refreshJobs = async () => {
    if (!USE_GOBODO_API) {
      scheduledJobsStorage.initializeWithMockData(mockScheduledJobs, mockJobExecutions);
    }
    const list = await scheduledJobsStorageRouter.getAllJobsAsync();
    setJobs(list);
    setIsLoading(false);
  };

  useEffect(() => {
    refreshJobs();
  }, []);

  // Filter jobs based on search
  const filteredJobs = useMemo(() => {
    if (!searchQuery) return jobs;

    const query = searchQuery.toLowerCase();
    return jobs.filter(job =>
      job.name.toLowerCase().includes(query) ||
      job.description.toLowerCase().includes(query) ||
      job.type.toLowerCase().includes(query)
    );
  }, [jobs, searchQuery]);

  const handleToggleStatus = async (jobId: string) => {
    try {
      const job = await scheduledJobsStorageRouter.toggleJobStatusAsync(jobId);
      if (job) {
        success(job.isActive ? 'Job Activated' : 'Job Paused', `${job.name} has been ${job.isActive ? 'activated' : 'paused'}`);
        await refreshJobs();
      }
    } catch (err) {
      showError('Error', 'Failed to toggle job status');
    }
  };

  const handleRunJob = (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      success('Job Started', `${job.name} is now running`);
      setTimeout(() => {
        success('Job Completed', `${job.name} completed successfully`);
        refreshJobs();
      }, 2000);
    }
  };

  // Handle delete job
  const handleDeleteJob = (jobId: string) => {
    setJobToDelete(jobId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!jobToDelete) {
      setIsDeleteModalOpen(false);
      setJobToDelete(null);
      return;
    }
    const job = jobs.find(j => j.id === jobToDelete);
    const deleted = await scheduledJobsStorageRouter.deleteJobAsync(jobToDelete);
    if (deleted && job) {
      success('Job Deleted', `${job.name} has been deleted`);
      await refreshJobs();
      setSelectedJobs(prev => {
        const next = new Set(prev);
        next.delete(jobToDelete);
        return next;
      });
    } else {
      showError('Error', 'Failed to delete job');
    }
    setIsDeleteModalOpen(false);
    setJobToDelete(null);
  };

  const handleBulkActivate = async () => {
    let count = 0;
    for (const jobId of Array.from(selectedJobs)) {
      const job = await scheduledJobsStorageRouter.getJobAsync(jobId);
      if (job && !job.isActive) {
        await scheduledJobsStorageRouter.toggleJobStatusAsync(jobId);
        count++;
      }
    }
    if (count > 0) {
      success('Jobs Activated', `${count} job(s) activated`);
      await refreshJobs();
      setSelectedJobs(new Set());
    }
  };

  const handleBulkPause = async () => {
    let count = 0;
    for (const jobId of Array.from(selectedJobs)) {
      const job = await scheduledJobsStorageRouter.getJobAsync(jobId);
      if (job && job.isActive) {
        await scheduledJobsStorageRouter.toggleJobStatusAsync(jobId);
        count++;
      }
    }
    if (count > 0) {
      success('Jobs Paused', `${count} job(s) paused`);
      await refreshJobs();
      setSelectedJobs(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = selectedJobs.size;
    for (const jobId of Array.from(selectedJobs)) {
      await scheduledJobsStorageRouter.deleteJobAsync(jobId);
    }
    success('Jobs Deleted', `${count} job(s) deleted`);
    await refreshJobs();
    setSelectedJobs(new Set());
  };

  // Table columns
  const columns: TableColumn<ScheduledJob>[] = [
    {
      key: 'name',
      label: 'Job Name',
      sortable: true,
      render: (value, job) => {
        const JobTypeIcon = jobTypeIcons[job.type];
        return (
          <div className="flex items-center gap-2">
            {JobTypeIcon && <JobTypeIcon size={16} className="text-gray-400" />}
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">
                {value}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {jobTypeLabels[job.type]}
              </div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'schedule',
      label: 'Schedule',
      render: (_, job) => {
        const { frequency, time } = job.schedule;
        return (
          <div className="text-sm">
            <div className="font-medium text-gray-900 dark:text-gray-100 capitalize">
              {frequency}
            </div>
            {time && (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                at {time}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (_, job) => {
        const statusConfig = {
          scheduled: {
            bg: 'bg-blue-100 dark:bg-blue-900/30',
            text: 'text-blue-800 dark:text-blue-200',
            icon: Clock
          },
          running: {
            bg: 'bg-yellow-100 dark:bg-yellow-900/30',
            text: 'text-yellow-800 dark:text-yellow-200',
            icon: Play
          },
          completed: {
            bg: 'bg-green-100 dark:bg-green-900/30',
            text: 'text-green-800 dark:text-green-200',
            icon: CheckCircle
          },
          failed: {
            bg: 'bg-red-100 dark:bg-red-900/30',
            text: 'text-red-800 dark:text-red-200',
            icon: XCircle
          },
          paused: {
            bg: 'bg-gray-100 dark:bg-gray-800',
            text: 'text-gray-800 dark:text-gray-200',
            icon: Pause
          }
        };

        const config = statusConfig[job.status];
        const StatusIcon = config.icon;

        return (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
            <StatusIcon size={12} />
            {job.status}
          </span>
        );
      }
    },
    {
      key: 'lastRun',
      label: 'Last Run',
      render: (value) => {
        if (!value) return <span className="text-gray-400">Never</span>;
        return (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {new Date(value).toLocaleString()}
          </div>
        );
      }
    },
    {
      key: 'nextRun',
      label: 'Next Run',
      render: (value) => {
        if (!value) return <span className="text-gray-400">Not scheduled</span>;
        return (
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {new Date(value).toLocaleString()}
          </div>
        );
      }
    },
    {
      key: 'id',
      label: 'Actions',
      render: (_, job) => (
        <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          <StandardButton
            size="xs"
            variant="ghost"
            onClick={() => handleRunJob(job.id)}
            title="Run now"
          >
            <Play size={14} />
          </StandardButton>
          <StandardButton
            size="xs"
            variant="ghost"
            onClick={() => handleToggleStatus(job.id)}
            title={job.isActive ? 'Pause' : 'Activate'}
          >
            {job.isActive ? <Pause size={14} /> : <Play size={14} />}
          </StandardButton>
          <StandardButton
            size="xs"
            variant="ghost"
            onClick={() => handleDeleteJob(job.id)}
            title="Delete"
          >
            <Trash2 size={14} className="text-red-600" />
          </StandardButton>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <StandardCard>
        <StandardCard.Body>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Scheduled Jobs
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Automate tasks with scheduled job execution
              </p>
            </div>
            <StandardButton
              variant="primary"
              leftIcon={<Plus size={16} />}
              onClick={onCreateJob}
              data-testid="new-scheduled-job-btn"
            >
              Create Job
            </StandardButton>
          </div>

          {/* Search and Bulk Actions */}
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <StandardSearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search jobs by name, type, or description..."
              />
            </div>

            {selectedJobs.size > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedJobs.size} selected
                </span>
                <StandardButton
                  size="sm"
                  variant="secondary"
                  leftIcon={<Play size={14} />}
                  onClick={handleBulkActivate}
                >
                  Activate
                </StandardButton>
                <StandardButton
                  size="sm"
                  variant="secondary"
                  leftIcon={<Pause size={14} />}
                  onClick={handleBulkPause}
                >
                  Pause
                </StandardButton>
                <StandardButton
                  size="sm"
                  variant="danger"
                  leftIcon={<Trash2 size={14} />}
                  onClick={handleBulkDelete}
                >
                  Delete
                </StandardButton>
              </div>
            )}
          </div>
        </StandardCard.Body>
      </StandardCard>

      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
        <p className="text-sm text-yellow-700 dark:text-yellow-300">
          Showing {jobs.length} job{jobs.length === 1 ? '' : 's'} persisted via{' '}
          {USE_GOBODO_API ? 'Gobodo API' : 'scheduledJobsStorage (localStorage)'}.
        </p>
      </div>

      {/* Jobs Table */}
      <StandardCard padding="none">
        {isLoading ? (
          <StandardLoadingState message="Loading jobs..." />
        ) : filteredJobs.length === 0 && !searchQuery ? (
          <StandardEmptyState
            icon={<Clock size={48} />}
            title="No scheduled jobs"
            message="Get started by creating your first scheduled job"
            actionLabel="Create Job"
            onAction={onCreateJob}
          />
        ) : filteredJobs.length === 0 ? (
          <StandardEmptyState
            icon={<AlertCircle size={48} />}
            title="No jobs found"
            message={`No jobs match "${searchQuery}"`}
          />
        ) : (
          <StandardDataTable
            columns={columns}
            data={filteredJobs}
            selectable
            selectedRows={selectedJobs}
            onSelectionChange={setSelectedJobs}
            onRowClick={(job) => onViewJob(job.id)}
          />
        )}
      </StandardCard>

      {/* Delete Confirmation Modal */}
      <StandardModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Scheduled Job"
        size="md"
      >
        <StandardModal.Body>
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertCircle className="text-red-600 dark:text-red-400" size={20} />
            </div>
            <div>
              <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                Are you sure you want to delete this scheduled job?
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone. The job will be permanently removed and will no longer execute.
              </p>
            </div>
          </div>
        </StandardModal.Body>
        <StandardModal.Footer>
          <StandardButton
            variant="secondary"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="danger"
            onClick={confirmDelete}
          >
            Delete Job
          </StandardButton>
        </StandardModal.Footer>
      </StandardModal>
    </div>
  );
};

export default ScheduledJobsList;
